import React, { useState, useEffect } from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Upgrade as UpgradeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../utils/api';

interface UpgradeButtonProps extends Omit<ButtonProps, 'onClick'> {
  plan?: 'paid' | 'pro';
  redirectToCheckout?: boolean;
  showSnackbar?: boolean;
}

interface SubscriptionStatus {
  user_type: string;
  subscription_status?: string;
  trial_expires_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_trial_expired: boolean;
  meetings_count: number;
  trial_meetings_limit: number;
}

const UpgradeButton: React.FC<UpgradeButtonProps> = ({
  plan = 'paid',
  redirectToCheckout = false,
  showSnackbar = true,
  children,
  ...buttonProps
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Error loading subscription status:', err);
    }
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!redirectToCheckout) {
      navigate('/pricing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create checkout session
      const response = await api.post('/subscription/create-checkout', {
        price_id: `price_${plan}`, // This needs to be actual Stripe price ID
        success_url: `${window.location.origin}/dashboard?upgrade=success`,
        cancel_url: `${window.location.origin}/dashboard?upgrade=cancelled`,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start upgrade process');
      setLoading(false);
    }
  };

  // Don't show button if user is already on paid plan or higher
  if (subscriptionStatus && ['paid', 'pro', 'admin'].includes(subscriptionStatus.user_type)) {
    return null;
  }

  const buttonText = children || `Upgrade to ${plan === 'paid' ? 'Paid' : 'Pro'}`;

  return (
    <>
      <Button
        {...buttonProps}
        onClick={handleUpgrade}
        disabled={loading || buttonProps.disabled}
        startIcon={loading ? <CircularProgress size={16} /> : <UpgradeIcon />}
      >
        {loading ? 'Processing...' : buttonText}
      </Button>

      {/* Error Snackbar */}
      {showSnackbar && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default UpgradeButton; 